<template>
  <Drawer
    :visible="visible"
    position="right"
    class="archive-detail-drawer"
    :pt="drawerPt"
    :style="{ width: '480px', maxWidth: '100vw' }"
    data-testid="archive-detail-drawer"
    @update:visible="$emit('update:visible', $event)"
  >
    <template #header>
      <div v-if="entry" class="archive-detail-drawer__header">
        <div class="archive-detail-drawer__badges">
          <span class="archive-detail-drawer__type-badge">{{ typeLabel }}</span>
          <span
            class="archive-detail-drawer__status-badge"
            :class="`archive-detail-drawer__status-badge--${statusKey}`"
          >
            {{ statusLabel }}
          </span>
        </div>
        <h2 class="archive-detail-drawer__title">{{ entry.title }}</h2>
        <div class="archive-detail-drawer__readonly">
          <i class="pi pi-lock text-xs" aria-hidden="true" />
          <span>{{ $t('UserProjects.archive.drawer.readOnly') }}</span>
        </div>
      </div>
    </template>

    <div v-if="entry" class="archive-detail-drawer__body">
      <div class="archive-detail-drawer__meta-grid">
        <div>
          <p class="archive-detail-drawer__meta-label">{{ completedAtLabel }}</p>
          <p class="archive-detail-drawer__meta-value">{{ completedAtValue }}</p>
        </div>
        <div>
          <p class="archive-detail-drawer__meta-label">{{ $t('UserProjects.archive.drawer.owner') }}</p>
          <div class="archive-detail-drawer__owner">
            <span class="archive-detail-drawer__avatar">{{ ownerInitials }}</span>
            <span>{{ ownerName }}</span>
          </div>
        </div>
        <div>
          <p class="archive-detail-drawer__meta-label">{{ milestoneLabel }}</p>
          <p class="archive-detail-drawer__meta-value">{{ milestoneValue }}</p>
        </div>
        <div>
          <p class="archive-detail-drawer__meta-label">{{ $t('UserProjects.archive.drawer.duration') }}</p>
          <p class="archive-detail-drawer__meta-value">{{ durationValue }}</p>
        </div>
      </div>

      <div
        v-for="section in detailSections"
        :key="section.key"
        class="archive-detail-drawer__section"
      >
        <p class="archive-detail-drawer__section-label">{{ section.label }}</p>
        <p class="archive-detail-drawer__section-text">{{ section.text }}</p>
      </div>
    </div>

    <template #footer>
      <div class="archive-detail-drawer__footer">
        <Button
          type="button"
          :label="$t('UserProjects.archive.drawer.close')"
          severity="secondary"
          outlined
          data-testid="archive-drawer-close"
          @click="close"
        />
      </div>
    </template>
  </Drawer>
</template>

<script>
import Drawer from 'primevue/drawer';
import Button from 'primevue/button';
import {
  archiveTypeLabelKey,
  computeArchiveDurationWeeks,
  formatArchiveCalendarWeek,
  formatArchiveDate,
  ownerInitialsFromName,
  resolveArchiveCategoryLabel,
} from '@/utils/archiveDisplay';

export default {
  name: 'ArchiveDetailDrawer',
  components: {
    Drawer,
    Button,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    entry: {
      type: Object,
      default: null,
    },
    userLookup: {
      type: Object,
      default: () => ({}),
    },
    milestoneLookup: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['update:visible'],
  computed: {
    drawerPt() {
      return {
        root: { 'data-testid': 'archive-detail-drawer' },
      };
    },
    raw() {
      return this.entry?.raw || {};
    },
    typeLabel() {
      return this.entry ? this.$t(archiveTypeLabelKey(this.entry.entityType)) : '';
    },
    statusKey() {
      if (!this.entry) return 'done';
      if (this.entry.entityType === 'constraints') return 'resolved';
      if (this.entry.entityType === 'decisions') return 'decided';
      if (this.entry.entityType === 'commitments' && this.entry.status === 'not_done') return 'not_done';
      return 'done';
    },
    statusLabel() {
      return this.$t(`UserProjects.archive.drawer.status.${this.statusKey}`);
    },
    ownerName() {
      const id = this.entry?.ownerUserId;
      return this.entry?.ownerDisplayName
        || (id ? this.userLookup[id] : null)
        || '—';
    },
    ownerInitials() {
      return ownerInitialsFromName(this.ownerName);
    },
    completedAtLabel() {
      if (this.entry?.entityType === 'constraints') {
        return this.$t('UserProjects.archive.drawer.resolvedAt');
      }
      if (this.entry?.entityType === 'decisions') {
        return this.$t('UserProjects.archive.drawer.decidedAt');
      }
      return this.$t('UserProjects.archive.drawer.completedAt');
    },
    completedAtValue() {
      const sortDate = this.entry?.sortDate;
      const week = formatArchiveCalendarWeek(sortDate);
      const date = formatArchiveDate(sortDate, this.$i18n?.locale);
      return `${week} · ${date}`;
    },
    milestoneLabel() {
      if (this.entry?.entityType === 'constraints') {
        return this.$t('UserProjects.archive.drawer.category');
      }
      return this.$t('UserProjects.archive.drawer.milestone');
    },
    milestoneValue() {
      if (this.entry?.entityType === 'constraints') {
        return resolveArchiveCategoryLabel(this.entry.category || this.raw.category, this.$t.bind(this));
      }
      const title = this.entry?.milestoneTitle
        || (this.entry?.milestoneId ? this.milestoneLookup[this.entry.milestoneId] : null);
      return title || '—';
    },
    durationValue() {
      if (this.entry?.entityType === 'constraints') {
        const days = this.raw.daysOpen;
        if (days == null) return '—';
        const weeks = Math.max(1, Math.round(Number(days) / 7));
        return this.$t('UserProjects.archive.drawer.durationWeeks', { count: weeks });
      }
      if (this.entry?.entityType === 'milestones') {
        const deviation = this.raw.deviationDays;
        if (deviation == null) return '—';
        return this.$t('UserProjects.archive.drawer.deviationDays', { count: deviation });
      }
      const start = this.raw.date_created || this.raw.dueDate;
      const end = this.raw.doneDate || this.entry?.sortDate;
      const weeks = computeArchiveDurationWeeks(start, end);
      if (weeks == null) return '—';
      return this.$t('UserProjects.archive.drawer.durationWeeks', { count: weeks });
    },
    detailSections() {
      if (!this.entry) return [];
      const sections = [];

      if (this.entry.entityType === 'commitments') {
        sections.push(
          {
            key: 'was',
            label: this.$t('UserProjects.archive.drawer.sections.commitmentWas'),
            text: this.raw.was || '—',
          },
          {
            key: 'recognizable',
            label: this.$t('UserProjects.archive.drawer.sections.commitmentRecognizable'),
            text: this.raw.woranErkennbar || '—',
          },
          {
            key: 'next',
            label: this.$t('UserProjects.archive.drawer.sections.commitmentNextStep'),
            text: this.raw.wasKonkret || '—',
          },
        );
        if (this.raw.completionComment) {
          sections.push({
            key: 'comment',
            label: this.$t('UserProjects.archive.drawer.sections.completionComment'),
            text: this.raw.completionComment,
          });
        }
        if (this.raw.cause?.freeText) {
          sections.push({
            key: 'cause',
            label: this.$t('UserProjects.archive.drawer.sections.cause'),
            text: this.raw.cause.freeText,
          });
        }
      }

      if (this.entry.entityType === 'constraints') {
        if (this.raw.description) {
          sections.push({
            key: 'description',
            label: this.$t('UserProjects.archive.drawer.sections.constraintDescription'),
            text: this.raw.description,
          });
        }
        if (this.raw.solutionText) {
          sections.push({
            key: 'solution',
            label: this.$t('UserProjects.archive.drawer.sections.constraintSolution'),
            text: this.raw.solutionText,
          });
        }
      }

      if (this.entry.entityType === 'decisions') {
        sections.push(
          {
            key: 'basis',
            label: this.$t('UserProjects.archive.drawer.sections.decisionBasis'),
            text: this.raw.basis || '—',
          },
          {
            key: 'impact',
            label: this.$t('UserProjects.archive.drawer.sections.decisionImpact'),
            text: this.raw.impactIfDelayed || '—',
          },
        );
      }

      if (this.entry.entityType === 'milestones') {
        sections.push({
          key: 'summary',
          label: this.$t('UserProjects.archive.drawer.sections.milestoneSummary'),
          text: this.$t('UserProjects.archive.drawer.sections.milestoneCompletedHint', {
            date: formatArchiveDate(this.raw.actualDate || this.entry.sortDate, this.$i18n?.locale),
          }),
        });
      }

      return sections;
    },
  },
  methods: {
    close() {
      this.$emit('update:visible', false);
    },
  },
};
</script>

<style scoped>
.archive-detail-drawer__header {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
}

.archive-detail-drawer__badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

.archive-detail-drawer__type-badge {
  font-size: 10px;
  font-weight: 500;
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
  background: #f2f2f2;
  color: #6b6b6b;
}

.archive-detail-drawer__status-badge {
  font-size: 10px;
  font-weight: 500;
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
  color: #fff;
}

.archive-detail-drawer__status-badge--done,
.archive-detail-drawer__status-badge--resolved,
.archive-detail-drawer__status-badge--decided {
  background: #22c55e;
}

.archive-detail-drawer__status-badge--not_done {
  background: #ef4444;
}

.archive-detail-drawer__title {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: var(--color-dark);
  line-height: 1.35;
}

.archive-detail-drawer__readonly {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 12px;
  color: #ababab;
}

.archive-detail-drawer__body {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.archive-detail-drawer__meta-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 12px;
  background: #f9f9f9;
}

.archive-detail-drawer__meta-label {
  margin: 0 0 0.125rem;
  font-size: 11px;
  color: #ababab;
}

.archive-detail-drawer__meta-value {
  margin: 0;
  font-size: 13px;
  font-weight: 500;
  color: var(--color-dark);
}

.archive-detail-drawer__owner {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 13px;
  font-weight: 500;
  color: var(--color-dark);
}

.archive-detail-drawer__avatar {
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 999px;
  background: var(--color-orange);
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.archive-detail-drawer__section-label {
  margin: 0 0 0.375rem;
  font-size: 12px;
  font-weight: 500;
  color: var(--color-disabled);
}

.archive-detail-drawer__section-text {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: var(--color-dark);
  white-space: pre-wrap;
}

.archive-detail-drawer__footer {
  display: flex;
  justify-content: flex-end;
  width: 100%;
}
</style>
